/**
 * Estado de cada arquivo na tela de importação por PDF.
 *
 * Os dois primeiros ("na fila" e "processando") só existem no cliente: o
 * registro no banco nasce quando o job termina, então até lá não há status
 * de servidor para mostrar. Os demais espelham
 * POLICY_DOCUMENT_IMPORT_STATUSES do backend.
 */
export const FILE_STATUS = {
  na_fila: { label: "Na fila", color: "#9E9E9E" },
  processando: { label: "Processando", color: "#1976D2" },
  pendente_conferencia: { label: "Pendente conferência", color: "#2E7D32" },
  senha_pendente: { label: "Senha pendente", color: "#EF6C00" },
  erro_leitura: { label: "Erro de leitura", color: "#C62828" },
  efetivado: { label: "Efetivado", color: "#2E7D32" },
  ignorado: { label: "Ignorado", color: "#757575" },
  // Só no cliente: o backend recusou o PDF (já importado), sem registro nem socket.
  ja_importado: { label: "Já importado", color: "#757575" },
};

export const statusLabel = (status) => (FILE_STATUS[status] || FILE_STATUS.na_fila).label;
export const statusColor = (status) => (FILE_STATUS[status] || FILE_STATUS.na_fila).color;

/** Terminou de processar — não espera mais evento de socket para este arquivo. */
const SETTLED = ["pendente_conferencia", "senha_pendente", "erro_leitura", "efetivado", "ignorado", "ja_importado"];

export const isSettled = (status) => SETTLED.includes(status);

/**
 * Motivos de erro que o backend grava em errorReason. Texto curto e genérico
 * de propósito: nunca deve conter conteúdo do PDF nem dado do segurado.
 */
const ERROR_REASON_LABEL = {
  senha_invalida: "Senha inválida ou ausente",
  pdf_corrompido: "Arquivo corrompido ou ilegível",
  pdf_sem_texto: "PDF digitalizado, sem texto selecionável",
  ia_indisponivel: "Leitura automática indisponível — preencha na conferência",
  falha_processamento: "Falha ao processar — tente enviar novamente",
  documento_muito_grande: "Documento grande demais para a leitura automática.",
  resposta_invalida: "A leitura automática não retornou dados válidos.",
  ia_limite_excedido: "Limite da leitura automática atingido. Tente de novo mais tarde.",
  api_key_ausente: "Leitura automática indisponível. Avise o administrador.",
};

export const errorReasonLabel = (reason) => (reason ? ERROR_REASON_LABEL[reason] || "Não foi possível ler automaticamente" : "");

export const MAX_FILES = 50;
export const MAX_FILE_BYTES = 10 * 1024 * 1024;

/**
 * Valida a seleção antes de enviar qualquer coisa. Devolve os aceitos e uma
 * lista de recusados com o motivo, para a tela explicar em vez de falhar
 * silenciosamente. O servidor revalida tudo (tamanho e MIME) — isto é só
 * para dar resposta imediata.
 */
export function validateSelection(files, alreadyQueued = 0) {
  const accepted = [];
  const rejected = [];

  files.forEach((file) => {
    if (file.size > MAX_FILE_BYTES) {
      rejected.push({ name: file.name, reason: "Maior que 10 MB" });
      return;
    }
    if (accepted.length + alreadyQueued >= MAX_FILES) {
      rejected.push({ name: file.name, reason: `Limite de ${MAX_FILES} arquivos por envio` });
      return;
    }
    accepted.push(file);
  });

  return { accepted, rejected };
}

/** Id de correlação do arquivo — o socket devolve este valor para a tela
 * saber qual linha atualizar. Nome de arquivo não serve: pode repetir. */
export function makeClientFileId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** UUID v4 para o lote. O backend só aceita UUID neste parâmetro — ele vira
 * nome de pasta em disco. */
export function makeBatchId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function summarize(files) {
  const total = files.length;
  const done = files.filter((file) => isSettled(file.status)).length;
  return { total, done };
}
